'use client'

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface MarqueeSignProps {
    /** Distance from the sign's edge to the bulb centers, in px. */
    inset?: number
    className?: string
    children: ReactNode
}

interface BulbPosition {
    left: number
    top: number
    delayed: boolean
}

/**
 * Evenly spaces chase-light bulbs around the sign's perimeter — one fixed
 * bulb-to-bulb pitch on all four sides, corners included.
 */
function computeBulbs(width: number, height: number, inset: number): BulbPosition[] {
    if (!width || !height) {
        return []
    }
    const pitch = 24
    const spanX = width - 2 * inset
    const spanY = height - 2 * inset
    const nx = Math.max(1, Math.round(spanX / pitch))
    const ny = Math.max(1, Math.round(spanY / pitch))
    const points: [number, number][] = []
    for (let i = 0; i <= nx; i++) {
        points.push([inset + (spanX * i) / nx, inset]) // top edge incl. both corners
    }
    for (let i = 1; i < ny; i++) {
        points.push([width - inset, inset + (spanY * i) / ny]) // right edge interior
    }
    for (let i = nx; i >= 0; i--) {
        points.push([inset + (spanX * i) / nx, height - inset]) // bottom edge incl. both corners
    }
    for (let i = ny - 1; i >= 1; i--) {
        points.push([inset, inset + (spanY * i) / ny]) // left edge interior
    }
    return points.map(([x, y], i) => ({
        left: Math.round(x),
        top: Math.round(y),
        delayed: i % 2 === 1,
    }))
}

/**
 * Cinema-marquee frame: alternating chase bulbs around whatever it wraps.
 * Bulb positions re-measure on resize; the chase animation is CSS
 * (`.imax-mq-bulb`) and freezes under prefers-reduced-motion.
 */
export const MarqueeSign = ({ inset = 8, className, children }: MarqueeSignProps) => {
    const ref = useRef<HTMLDivElement>(null)
    const [size, setSize] = useState({ width: 0, height: 0 })

    useEffect(() => {
        const el = ref.current
        if (!el) {
            return
        }
        const measure = () => {
            setSize((prev) => {
                const width = el.offsetWidth
                const height = el.offsetHeight
                return prev.width === width && prev.height === height ? prev : { width, height }
            })
        }
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(el)
        return () => {
            observer.disconnect()
        }
    }, [])

    const bulbs = useMemo(
        () => computeBulbs(size.width, size.height, inset),
        [size.width, size.height, inset],
    )

    return (
        <div ref={ref} className={cn('relative', className)}>
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                {bulbs.map((bulb, i) => (
                    <span
                        key={i}
                        className="imax-mq-bulb"
                        style={{
                            left: bulb.left,
                            top: bulb.top,
                            animationDelay: bulb.delayed ? '0.35s' : '0s',
                        }}
                    />
                ))}
            </div>
            {children}
        </div>
    )
}
